
import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { Mic, Upload, Square, Volume2, Loader2 } from 'lucide-react';
import { useIsMobile } from '@/hooks/use-mobile';
import { ApiTranscriptionData } from '@/types/sections';
import { MicrophoneErrorDialog } from '@/components/MicrophoneErrorDialog';
import { VolumeWaveIndicator } from '@/components/VolumeWaveIndicator';

interface ConsultationDialogProps {
  isRecording?: boolean;
  showContinueButton: boolean;
  consultationDialogOpen: boolean;
  microphoneErrorOpen: boolean;
  isLoading?: boolean;
  isNewConsultation?: boolean; // Nuevo prop para diferenciar
  currentAnalysisId?: number; // ID del análisis actual
  onStartRecording: () => void;
  onStopRecording: () => void;
  onContinueConsultation: () => void;
  onFinishConsultation: () => void;
  onUploadFile: () => void;
  onOpenChange: (open: boolean) => void;
  onMicrophoneErrorChange: (open: boolean) => void;
  onRetryMicrophone: () => void;
  onFileTranscriptionComplete?: (transcriptions: ApiTranscriptionData[], title: string, analysisId: number) => void;
  template?: any;
}

export const ConsultationDialog = ({ 
  isRecording = false,
  showContinueButton,
  consultationDialogOpen,
  microphoneErrorOpen,
  isLoading = false,
  isNewConsultation = true,
  currentAnalysisId,
  onStartRecording,
  onStopRecording,
  onContinueConsultation,
  onFinishConsultation,
  onUploadFile,
  onOpenChange,
  onMicrophoneErrorChange,
  onRetryMicrophone,
  onFileTranscriptionComplete,
  template
}: ConsultationDialogProps) => {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  const handleUploadFileClick = () => {
    console.log('📄 Upload file clicked - calling onUploadFile');
    onOpenChange(false); // Close consultation dialog
    onUploadFile(); // This should trigger the file upload modal
  };

  const handleStartRecordingWithErrorHandling = () => {
    console.log('🎤 [CONSULTATION] Starting recording with error handling');
    console.log('🎤 [CONSULTATION] Is new consultation?', isNewConsultation);
    console.log('🎤 [CONSULTATION] Current analysis ID:', currentAnalysisId);
    onOpenChange(false); // Close consultation dialog
    onStartRecording();
  };

  const handleContinueConsultationClick = () => {
    console.log('🎤 [CONSULTATION] Continuing consultation - should NOT create new analysis');
    console.log('🎤 [CONSULTATION] Current analysis ID:', currentAnalysisId);
    onContinueConsultation();
  };

  // Si está grabando, mostrar controles de grabación
  if (isRecording) {
    return (
      <>
        <div className="space-y-4">
          <div className="text-center space-y-2">
            <div className="flex items-center justify-center space-x-2">
              <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
              <span className="text-sm text-muted-foreground">{t('recording_consultation')}...</span>
            </div>
          </div>
          
          <div className="flex items-center justify-center py-2">
            <VolumeWaveIndicator isActive={true} barCount={10} className="w-24" />
          </div>
          
          <Button 
            onClick={onStopRecording}
            className="w-full bg-orange-500 text-white hover:bg-orange-600 rounded-full"
          >
            <Square className="w-4 h-4 mr-2" />
            {t('stop')}
          </Button>

          <div className="text-center">
            <button
              onClick={onFinishConsultation}
              className="text-sm text-muted-foreground hover:text-foreground underline"
            >
              {t('finish_consultation')}
            </button>
          </div>
        </div>

        <MicrophoneErrorDialog
          open={microphoneErrorOpen}
          onOpenChange={onMicrophoneErrorChange}
          onRetry={onRetryMicrophone}
        />
      </>
    );
  }

  // Si debe mostrar botón continuar (después de parar grabación)
  if (showContinueButton) {
    return (
      <>
        <div className="space-y-4">
          <div className="text-center text-sm text-muted-foreground mb-4">
            {t('start_analysis_when_consultation_begins')}
          </div>
          
          <Button 
            onClick={handleContinueConsultationClick}
            className="w-full bg-green-600 text-white hover:bg-green-700 rounded-full"
          >
            {t('continue_consultation')}
          </Button>

          <div className="text-center">
            <button
              onClick={onFinishConsultation}
              className="text-sm text-muted-foreground hover:text-foreground underline"
            >
              {t('finish_consultation')}
            </button>
          </div>
        </div>

        <MicrophoneErrorDialog
          open={microphoneErrorOpen}
          onOpenChange={onMicrophoneErrorChange}
          onRetry={onRetryMicrophone}
        />
      </>
    );
  }

  // Estado inicial - mostrar botón "Iniciar nova consulta" con popover
  return (
    <>
      <div className="space-y-4">
        <div className="text-center text-sm text-muted-foreground mb-4">
          {t('start_analysis_when_consultation_begins')}
        </div>
        
        <Popover open={consultationDialogOpen} onOpenChange={onOpenChange}>
          <PopoverTrigger asChild>
            <Button 
              className="w-full bg-black text-white hover:bg-gray-800 rounded-full"
              disabled={isLoading}
            >
              {isLoading ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  {t('loading')}...
                </>
              ) : (
                t('start_new_consultation')
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent 
            className="w-64 p-2 z-[10000]"
            side="top"
            align="end"
            sideOffset={8}
            alignOffset={isMobile ? -40 : -80}
          >
            <button
              onClick={handleStartRecordingWithErrorHandling}
              disabled={isLoading}
              className="flex items-center gap-3 w-full text-left px-4 py-3 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Mic className="w-5 h-5 text-gray-700" />
              <span className="text-gray-800 font-medium">Record</span>
            </button>

            <button
              onClick={handleUploadFileClick}
              disabled={isLoading}
              className="flex items-center gap-3 w-full text-left px-4 py-3 rounded-md hover:bg-gray-100 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <Upload className="w-5 h-5 text-gray-700" />
              <span className="text-gray-800 font-medium">Upload file</span>
            </button>
          </PopoverContent>
        </Popover>
      </div>

      <MicrophoneErrorDialog
        open={microphoneErrorOpen}
        onOpenChange={onMicrophoneErrorChange}
        onRetry={onRetryMicrophone}
      />
    </>
  );
};
